import React, { useState } from 'react';

const TaskItem = ({ task, onTaskClick, level, onToggleComplete, onAddSubTask, unitId, unitName, unitColor: unitColorProp, onSelectBusinessUnit, deriveBusinessUnitId, businessUnitMap = {} }) => {
    const [expanded, setExpanded] = useState(true);
    const children = task.sub_tasks || task.subTasks || [];
    const hasChildren = children.length > 0;

    const isCompleted = task.properties?.completed === 'yes';
    const statusValue = (task.properties?.status || '').toLowerCase();
    const isReviewTask = task.properties?.reviewtask == 1;
    const isInReview = statusValue === 'in review' || statusValue === 'in_review';
    const isRejected = statusValue === 'rejected';
    const isStructured = !!task.object_definitions_id && !isReviewTask;
    const derivedUnitId = unitId || (deriveBusinessUnitId ? deriveBusinessUnitId(task) : '');
    const fallbackUnitId = task.unit_id || task.unitId || '';
    const resolvedUnitId = derivedUnitId ? String(derivedUnitId) : (fallbackUnitId ? String(fallbackUnitId) : '');
    const resolvedUnit = resolvedUnitId && businessUnitMap ? businessUnitMap[resolvedUnitId] : null;
    const unitColor = unitColorProp || task.unit_color || (resolvedUnit && (resolvedUnit.color || resolvedUnit.colour || resolvedUnit.display_color || resolvedUnit.displayColour));
    const unitLabel = unitName || (resolvedUnit && (resolvedUnit.title || resolvedUnit.name)) || 'Business Unit';

    const stripHtml = (value) => {
        if (!value) return '';
        return value.replace(/<[^>]*>?/gm, '');
    };

    const hasIncompleteChildren = hasChildren && children.some(child => child.properties?.completed !== 'yes');
    const canComplete = !hasIncompleteChildren;

    const handleCheckboxClick = (e) => {
        e.stopPropagation();
        if (!canComplete && !isCompleted) return;
        if (onToggleComplete) onToggleComplete(task);
    };

    const handleUnitClick = (e) => {
        e.stopPropagation();
        if (onSelectBusinessUnit && resolvedUnitId) {
            onSelectBusinessUnit(resolvedUnitId);
        }
    };

    const renderChildren = () => {
        if (!hasChildren || !expanded) return null;
        return children.map(child => (
            <TaskItem
                key={child.id}
                task={child}
                onTaskClick={onTaskClick}
                level={level + 1}
                onToggleComplete={onToggleComplete}
                onAddSubTask={onAddSubTask}
                onSelectBusinessUnit={onSelectBusinessUnit}
                deriveBusinessUnitId={deriveBusinessUnitId}
                businessUnitMap={businessUnitMap}
            />
        ));
    };

    const dueDate = task.date_due ? new Date(task.date_due).toLocaleDateString() : '-';
    const createdDate = task.created_at ? new Date(task.created_at).toLocaleDateString() : '-';
    const plainDescription = stripHtml(task.description);
    const isOverdue = task.date_due && !isCompleted && new Date(task.date_due) < new Date();

    const statusLabel = isCompleted
        ? 'Completed'
        : isRejected
            ? 'Rejected'
            : isInReview
                ? 'In Review'
                : 'In Progress';

    const statusClass = isCompleted
        ? 'is-complete'
        : isRejected
            ? 'is-rejected'
            : isInReview
                ? 'is-review'
                : 'is-progress';

    const rowStyle = {};

    const indentStepPx = 18;
    const indentBasePaddingPx = 32;
    const indentBaseLineLeftPx = 16;
    const indentLevel = Math.max(0, Number(level) || 0);
    const indentStyle = indentLevel > 0
        ? {
            '--task-indent-padding': `${indentBasePaddingPx + (indentLevel - 1) * indentStepPx}px`,
            '--task-indent-line-left': `${indentBaseLineLeftPx + (indentLevel - 1) * indentStepPx}px`,
        }
        : undefined;

    return (
        <>
            <tr
                className={`task-row ${isCompleted ? 'completed' : ''}`}
                style={rowStyle}
                onClick={() => onTaskClick(task.id)}
            >
                <td style={{ width: '30%', position: 'relative', paddingLeft: unitColor ? '16px' : undefined }}>
                    {unitColor && (
                        <button
                            type="button"
                            className="task-unit-bar"
                            style={{ background: unitColor }}
                            title={unitLabel}
                            aria-label={`Filter by ${unitLabel}`}
                            onClick={handleUnitClick}
                        />
                    )}
                    <div
                        className={`${indentLevel > 0 ? 'task-row-indented' : ''}`}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', ...(indentStyle || {}) }}
                    >
                        {hasChildren ? (
                            <button
                                className="task-expand-toggle"
                                onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
                                aria-label={expanded ? 'Collapse task' : 'Expand task'}
                            >
                                <i className={`fa ${expanded ? 'fa-caret-down' : 'fa-caret-right'}`}></i>
                            </button>
                        ) : (
                            <span style={{ width: '24px' }}></span>
                        )}
                        <div className="task-badge-color">
                            {unitColor && <span className="task-badge-dot" style={{ background: unitColor }} title={unitLabel}></span>}
                            <p className="task-title" style={{ margin: 0 }}>{task.title}</p>
                        </div>
                    </div>
                </td>
                <td style={{ maxWidth: '280px' }}>
                    {plainDescription ? (
                        <div
                            className="task-description"
                            data-tooltip={plainDescription}
                        >
                            {plainDescription}
                        </div>
                    ) : (
                        <span className="text-muted" style={{ fontStyle: 'italic', fontSize: '13px' }}>No description</span>
                    )}
                </td>
                <td style={{ color: '#94a3b8', fontSize: '12px' }}>{createdDate}</td>
                <td style={{ color: isOverdue ? '#ef4444' : '#475569', fontSize: '12px', fontWeight: isOverdue ? 600 : 400 }}>{dueDate}</td>
                <td style={{ textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <span className={`task-status-pill ${statusClass}`}>{statusLabel}</span>
                        <div
                            className={`task-checkbox ${isCompleted ? 'checked' : ''} ${canComplete ? '' : 'disabled'}`}
                            onClick={handleCheckboxClick}
                            title={!canComplete && !isCompleted ? 'Complete all sub-tasks first' : ''}
                        >
                            {isCompleted && <i className="fa fa-check"></i>}
                        </div>
                    </div>
                </td>
                <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    <button
                        className="task-view-btn"
                        onClick={(e) => { e.stopPropagation(); onTaskClick(task.id); }}
                        title="Open details"
                    >
                        <i className="fa fa-eye"></i>
                    </button>
                    <button
                        className="task-add-btn"
                        onClick={(e) => { e.stopPropagation(); onAddSubTask(task.id); }}
                        title="Add Sub-task"
                    >
                        <i className="fa fa-plus"></i>
                    </button>
                </td>
            </tr>
            {renderChildren()}
        </>
    );
};

export default TaskItem;
